import React, { useState } from "react";
import { Button } from "primereact/button";

import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { StepCallBack } from "@modules/plaintes/plaintes.model.ts";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  datestepSeven,
  instruction,
  loadingSupport,
  recordValid,
  sendTo,
  setDate,
  setSelectedInstruction,
  setSelectedSendTo,
  setTextArea,
  stepReasonValue,
  supportLists,
  textAreaStepSeven,
} from "@core/slice/ComplaintSlice.ts";
import {
  getSupportType,
} from "@core/actions/complaintAction.ts";
import { dataUser, profilTransfer } from "@core/slice/UsersSlice.ts";
import Message from "@shared/message/Message.tsx";
import { FetchStatusType } from "@core/modeles/model.ts";
import { getProfilLookup, getProfilTransfer } from "@core/actions/usersActions.ts";
import { MultiSelect } from "primereact/multiselect";
import { addLocale } from "primereact/api";
import { classNames } from "primereact/utils";
import ReadingAloud from "@modules/plaintes/complaintManaging/addingComplaint/dialog/ReadingAloud.tsx";

const Signature: React.FC<StepCallBack> = ({ onNext, onPrevious }) => {
  const dispatch = useAppDispatch();
  const stepReason = useAppSelector(stepReasonValue);
  const userInfos = useAppSelector(dataUser);
  const profil = useAppSelector(profilTransfer);
  const listsSupport = useAppSelector(supportLists);
  const loadingSelect = useAppSelector(loadingSupport);
  const date = useAppSelector(datestepSeven);
  const textAreaValue = useAppSelector(textAreaStepSeven);
  const selectedInstruction = useAppSelector(instruction);
  const selectedSendTo = useAppSelector(sendTo);
  const validRecord = useAppSelector(recordValid);
  const [complaintResponsable, setComplaintResponsable] = useState("");
  const [open, setOpen] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState<boolean>(false);
  const [messageInfo, setMessageInfo] = useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });
  const [errors, setErrors] = React.useState({
    instruction: "",
    send: "",
    action: "",
  });

  const isCompleteInfo =
    selectedInstruction.length !== 0 &&
    selectedSendTo !== undefined &&
    textAreaValue !== "" &&
    validRecord === false;

  addLocale("fr", {
    firstDayOfWeek: 1,
    dayNames: [
      "dimanche",
      "lundi",
      "mardi",
      "mercredi",
      "jeudi",
      "vendredi",
      "samedi",
    ],
    dayNamesShort: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: [
      "Janvier ",
      "Février ",
      "Mars ",
      "Avril ",
      "Mai ",
      "Juin ",
      "Juillet ",
      "Août ",
      "Septembre ",
      "Octobre ",
      "Novembre ",
      "Décembre ",
    ],
    monthNamesShort: [
      "janv",
      "févr",
      "mars",
      "avr",
      "mai",
      "juin",
      "juil",
      "août",
      "sept",
      "oct",
      "nov",
      "déc",
    ],
    today: "Aujourd'hui",
    clear: "Effacer",
  });

  const typeOrder = [
    "INTERNAL",
    "EXTERNAL",
    "ADMIN",
    "RGS",
    "CGP",
    "PFM",
    "COMPETENT_ENTITY",
  ];

  const sortedDropdown = profil.toSorted((a, b) => {
    const indexA = typeOrder.indexOf(a.type);
    const indexB = typeOrder.indexOf(b.type);
    return indexA - indexB;
  });

  React.useEffect(() => {
    if (userInfos) {
      const responsableAgentFullName = `${userInfos.lastName} ${userInfos.firstName}`;
      setComplaintResponsable(responsableAgentFullName);
    }
  }, [userInfos, stepReason]);

  React.useEffect(() => {
    dispatch(getProfilTransfer());
    // ReadingAloud, monté depuis cet écran, a besoin de la liste allégée des profils :
    // ne pas dépendre de l'étape « Mesure à prendre » pour l'avoir remplie.
    dispatch(getProfilLookup());
    dispatch(getSupportType());
  }, [dispatch]);

  function handlePreviousStep() {
    if (onPrevious) {
      onPrevious();
    }
  }

  const handleAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setTextArea(e.target.value));
    if (e.target.value !== "") {
      setErrors({ ...errors, action: "" });
    }
  };
  React.useEffect(() => {
    if (selectedInstruction.length !== 0) {
      setErrors({ ...errors, instruction: "" });
    }
    //eslint-disable-next-line
  }, [selectedInstruction]);

  React.useEffect(() => {
    if (selectedSendTo !== undefined) {
      setErrors({ ...errors, send: "" });
    }
    //eslint-disable-next-line
  }, [selectedSendTo]);

  return (
    <>
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <ReadingAloud
        isReadingAloud={isReadingAloud}
        setIsReadingAloud={setIsReadingAloud}
        isSignature
        setMessageInfo={setMessageInfo}
        setOpen={setOpen}
      />
      <div className="column gap-[20px] card-stepper">
        <div className="stitle">Les solutions à mettre en œuvre</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ">
          <div className="flex flex-wrap gap-3">
            <div className="flex-auto column gap-2 w-full">
              <label htmlFor="instruction">
                Instruction et prise en charge (obligatoire)
              </label>

              <MultiSelect
                id="instruction"
                value={selectedInstruction}
                onChange={(e: DropdownChangeEvent) =>
                  dispatch(setSelectedInstruction(e.value))
                }
                options={listsSupport}
                optionLabel="description"
                placeholder="---Prise en charge---"
                className="w-full md:w-14rem border border-solid border-[#d1d5db] h-[38px]"
                selectAllLabel="Tout séléctionner"
                loading={loadingSelect === FetchStatusType.LOADING}
              />
            </div>
            {errors.instruction && (
              <small className="text-red-600">{errors.instruction}</small>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex-auto column gap-2 w-full">
              <label htmlFor="sendTo">Envoyer vers (obligatoire) </label>

              <Dropdown
                id="sendTo"
                value={selectedSendTo}
                onChange={(e: DropdownChangeEvent) =>
                  dispatch(setSelectedSendTo(e.value))
                }
                options={sortedDropdown}
                optionLabel="name"
                placeholder="---envoyer vers---"
                className="w-full md:w-14rem"
              />
            </div>
            {errors.send && (
              <small className="text-red-600">{errors.send}</small>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex-auto column gap-2 w-full">
              <label htmlFor="date">Date</label>
              <Calendar
                locale="fr"
                placeholder="dd/mm/yyyy"
                id="buttondisplay"
                value={date}
                onChange={(e) => dispatch(setDate(e.value))}
                dateFormat="dd/mm/yy"
                showIcon
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="column gap-2 w-full">
              <label htmlFor="lastName">Nom de l'agent responsable</label>
              <InputText
                id="lastName"
                placeholder="RASOA"
                aria-describedby="username-help"
                // onChange={handleChangeInput}
                disabled
                value={complaintResponsable}
                maxLength={255}
              />
            </div>
          </div>
        </div>
        <div>
          <label htmlFor="complainantName" className="block font-medium mb-2">
            Action entreprise (obligatoire)
          </label>
          <InputTextarea
            value={textAreaValue}
            onChange={handleAreaChange}
            rows={5}
            placeholder="Entrez votre texte ici..."
            autoResize
            className="w-full"
          />
          {errors.action && (
            <small className="text-red-600">{errors.action}</small>
          )}
        </div>
        <div className="w-full flex justify-end gap-3 items-baseline">
          <Button
            label="Précédent"
            className="btn btn-secondary w-[120px]"
            onClick={handlePreviousStep}
          />
          <Button
            label="Lire à haute voix"
            className={classNames("btn btn-primary w-[150px]", {
              "btn-primary-disabled": !isCompleteInfo,
            })}
            disabled={!isCompleteInfo}
            onClick={() => setIsReadingAloud(true)}
          />
        </div>
      </div>
    </>
  );
};

export default Signature;
